import { useEffect, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { compareFamily, formatSize } from '@domain/product'
import type { PricePoint, Product, ProductFamily } from '@domain/product'
import { Glass } from '@ui/glass/Glass'
import { Voltar } from '@ui/shell/Voltar'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { ComparacaoDeFamilia } from '@ui/produtos/ComparacaoDeFamilia'
import { PilhaDeFotos } from '@ui/produtos/PilhaDeFotos'
import { Miniatura } from '@ui/produtos/Miniatura'
import { Botao } from '@ui/glass/Botao'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'
import { BotaoLembrar } from '@ui/lists/BotaoLembrar'

/**
 * Um grupo e todos os tamanhos dele. Tela empilhada sobre Produtos, como
 * `/produtos/$id` e `/notas/$id` — *"tela empilhada, não aba"*,
 * `Docs/04-Design/Navegação.md`.
 *
 * ## Ela mostra o grupo INTEIRO, e a lista mostrava só o vencedor
 *
 * Na lista, cada grupo cabe numa linha: o nome, quantas embalagens, e qual
 * compensa. Aqui cabe a lista completa — inclusive as que **não** entram na
 * comparação, que são justamente as que a pessoa precisa ver para saber por que
 * não entram (falta tamanho declarado, ou está em outra unidade).
 */
export function Grupo() {
  const { id } = useParams({ from: '/protegidas/grupos/$id' })
  const { productRepository } = useFinanceRepositories()

  const [dados, setDados] = useState<
    | { status: 'carregando' }
    | { status: 'falhou' }
    | { status: 'semGrupo' }
    | {
        status: 'pronto'
        familia: ProductFamily
        variantes: Product[]
        pontos: Map<string, PricePoint[]>
      }
  >({ status: 'carregando' })
  /** O nome em edição, ou `null`. A folha só existe enquanto ele existir. */
  const [renomeando, setRenomeando] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    void Promise.all([productRepository.listFamilies(), productRepository.list()])
      .then(async ([familias, produtos]) => {
        const familia = familias.find((f) => f.id === id)
        if (familia === undefined) {
          if (vivo) setDados({ status: 'semGrupo' })
          return
        }
        const variantes = produtos.filter((p) => p.familyId === id)
        const historicos = await Promise.all(
          variantes.map(async (p) => [p.id, await productRepository.priceHistory(p.id)] as const),
        )
        if (!vivo) return
        setDados({ status: 'pronto', familia, variantes, pontos: new Map(historicos) })
      })
      .catch((e: unknown) => {
        console.error('Grupo: a leitura falhou', e)
        if (vivo) setDados({ status: 'falhou' })
      })
    return () => {
      vivo = false
    }
  }, [productRepository, id])

  if (dados.status === 'carregando') {
    return <p className="text-sm text-[var(--text-muted)]">Carregando…</p>
  }
  if (dados.status === 'falhou') {
    return (
      <section>
        <Voltar para="/produtos">Produtos</Voltar>
        <p className="text-sm">Não foi possível carregar este grupo.</p>
      </section>
    )
  }
  if (dados.status === 'semGrupo') {
    return (
      <section>
        <Voltar para="/produtos">Produtos</Voltar>
        <p className="text-sm">Grupo não encontrado.</p>
      </section>
    )
  }

  const vivas = dados.variantes.filter((p) => p.archivedAt === null)
  const comparacao = compareFamily(dados.variantes, dados.pontos)

  const salvarNome = (nome: string) => {
    const limpo = nome.trim()
    if (limpo === '' || limpo === dados.familia.name) {
      setRenomeando(null)
      return
    }
    // Otimista, como a troca de família na página do produto: o pior caso de
    // falha é o nome voltar, e não um número de dinheiro errado.
    const anterior = dados.familia
    setDados({ ...dados, familia: { ...dados.familia, name: limpo } })
    setRenomeando(null)
    void productRepository.renameFamily(dados.familia.id, limpo).catch((erro: unknown) => {
      console.error('Grupo: não deu para renomear', erro)
      setDados({ ...dados, familia: anterior })
    })
  }

  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <Voltar para="/produtos">Produtos</Voltar>
        {/* Renomear é a única coisa editável de um grupo: uma família **é** um
            nome e um vínculo. Por isso o controle é um botão só, ao lado do
            voltar — a mesma linha de "o que dá para fazer a partir desta tela"
            que a página do produto usa (decisão 243). */}
        <span className="flex items-center gap-2">
          {/* Lembrar o GRUPO, e não uma embalagem: quem põe "Coca" na lista
              decide o tamanho na gôndola, que é a tese da decisão 202. */}
          <BotaoLembrar texto={dados.familia.name} familyId={dados.familia.id} />
          <Botao
            type="button"
            className="px-3.5 text-xs"
            onClick={() => {
              setRenomeando(dados.familia.name)
            }}
          >
            Renomear
          </Botao>
        </span>
      </div>

      <header className="mb-4 flex flex-wrap items-center gap-3">
        {/* A largura da coluna é de quem chama — ver o cabeçalho de `PilhaDeFotos`.
            Aqui é um cabeçalho, e 6rem é o que cabe ao lado do título. */}
        <span className="w-24 shrink-0">
          <PilhaDeFotos variantes={vivas} nome={dados.familia.name} />
        </span>
        <div>
          <h1 className="titulo text-2xl">{dados.familia.name}</h1>
          <p className="text-sm text-[var(--text-muted)]">
            {vivas.length === 1 ? '1 tamanho' : `${String(vivas.length)} tamanhos`}
          </p>
        </div>
      </header>

      <Glass level="frost" className="mb-4 p-4" as="section" aria-label="Comparação">
        <h2 className="mb-2 text-sm font-medium">Qual compensa</h2>
        <ComparacaoDeFamilia comparacao={comparacao} nome={dados.familia.name} />
      </Glass>

      <Glass level="frost" className="p-4" as="section" aria-label="Todos os tamanhos">
        <h2 className="mb-2 text-sm font-medium">Todos os tamanhos</h2>
        {vivas.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">
            Nenhum produto neste grupo. Abra um produto e escolha esta família em “Tamanhos”.
          </p>
        ) : (
          // A lista completa, com foto e sem ordem de preço — é a lista de
          // **o que existe**, e a de o que compensa está logo acima. Misturar
          // as duas faria a variação sem preço parecer a mais cara.
          <ul aria-label="Produtos do grupo">
            {vivas.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center gap-3 border-b border-[var(--border)] py-2 text-sm"
              >
                {/* Reserva o lugar mesmo sem foto: sem isso o nome de uma
                    variante sem imagem começava numa coluna e o da vizinha em
                    outra. Mesmo motivo da decisão 226(e). */}
                <Miniatura nome={p.name} src={p.imageUrl} className="size-9 text-xs" />
                <Link to="/produtos/$id" params={{ id: p.id }} className="min-w-0 flex-1 underline">
                  {p.name}
                </Link>
                <span className="text-xs text-[var(--text-muted)]">
                  {formatSize(p.size, p.unit, p.packQuantity) ?? 'sem tamanho'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Glass>

      {renomeando !== null && (
        <FolhaDeBaixo
          titulo="Renomear o grupo"
          onFechar={() => {
            setRenomeando(null)
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault()
              salvarNome(renomeando)
            }}
          >
            <label className="text-sm">
              <span className="block text-xs text-[var(--text-muted)]">Nome do grupo</span>
              {/* 16px, e não `text-sm`: abaixo disso o Safari do iPhone dá zoom
                  ao focar. Ver o piso dos campos em `app.css`. */}
              <input
                autoFocus
                value={renomeando}
                onChange={(e) => {
                  setRenomeando(e.target.value)
                }}
                aria-label="Nome do grupo"
                className="w-full rounded-[var(--radius-md)] border border-[var(--border-glass)] bg-transparent px-3 py-2.5 text-base"
              />
            </label>

            <p className="mt-2 text-xs text-[var(--text-faint)]">
              O nome é como o grupo aparece na busca e no seletor de família. Os produtos dentro
              dele não mudam.
            </p>

            <div className="mt-4 flex items-center gap-2">
              <Botao tom="primario" type="submit">
                Salvar
              </Botao>
              <Botao
                type="button"
                onClick={() => {
                  setRenomeando(null)
                }}
              >
                Cancelar
              </Botao>
            </div>
          </form>
        </FolhaDeBaixo>
      )}
    </section>
  )
}
